import React from 'react';
import PropTypes from 'prop-types';
import { fromGlobalId } from 'graphql-relay';
import get from 'lodash/get';
import { Link } from 'react-router';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formatLocationByPath } from '../../utils/location-utils';

import Item from '../../components/Item';
import Container from '../../components/Container';
import Button from '../../components/Button';
import Loader from '../../components/Loader';
import PageTitle from '../../components/PageTitle';
import Table from '../../components/Table';
import Icon from '../../components/Icon';
import Label from '../../components/Label';

import SubscriptionUpgradeForm from '../SubscriptionUpgradeForm';
import LockedContentComponent from './LockedContentComponent';
import SubscriptionActive from '../../components/SubscriptionActive';
import SubscriptionInactive from '../../components/SubscriptionInactive';
import { REPVISITS_LOOKING_FOR_YOU_COUNT, REP_VISITS_SUBSCRIPTION } from '../Authorization/constants';

import messages from './messages';
import './styles.css';

function getLocation(highSchool) {
  /* istanbul ignore if */
  if (!highSchool || !highSchool.repVisitsAccount) {
    return null;
  }

  if (highSchool.repVisitsAccount.availabilityVisible) {
    return `/rep-visits/search/${fromGlobalId(highSchool.id).id}/visits`;
  } else {
    return `/rep-visits/search/${fromGlobalId(highSchool.id).id}/fairs`;
  }
}

class SimplifiedIntermediateHighSchoolResults extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    viewer: PropTypes.object,
    loadMore: PropTypes.func,
    data: PropTypes.shape({
      variables: PropTypes.object,
    }),
    resultsTitle: PropTypes.string,
    queryName: PropTypes.string,
    showPageTitle: PropTypes.bool,
    scrollContextRef: PropTypes.object,
    scrollPosition: PropTypes.number,
    getScrollPositionForHSResults: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = { loading: false, showTooltip: false, isUpgradeFormVisible: false };
  }

  componentDidMount() {
    if (this.props.scrollContextRef) {
      this.props.scrollContextRef.scrollTop = this.props.scrollPosition;
    }
  }

  loadMore = () => {
    this.setState({ loading: true });
    this.props.loadMore(`viewer.${this.props.queryName}`).then(() => this.setState({ loading: false }));
  };

  affixCounty = (data) => {
    let modifiedData = data.toUpperCase();
    modifiedData = modifiedData.replace('PARISH', '');
    if (modifiedData.match('COUNTY')) {
      return modifiedData;
    } else {
      return `${modifiedData} COUNTY`;
    }
  }

  handleScrollPosition = () => {
    // If "international search result", do not re-render the search results, since the results appear under
    // the available High Schools
    if (!(this.props.resultsTitle && this.props.resultsTitle === 'International Schools')) {
      this.props.getScrollPositionForHSResults(this.props.scrollContextRef.scrollTop);
    }
  };

  toggleTooltip = () => {
    this.setState((previousState) => ({ showTooltip: !previousState.showTooltip }));
  }

  toggleUpgradeFormVisibility = () => (
    this.setState((previousState) => ({ isUpgradeFormVisible: !previousState.isUpgradeFormVisible }))
  );

  render() {
    const { viewer, queryName, resultsTitle, showPageTitle, intl } = this.props;
    const { enabledFeatures } = viewer;
    const { showTooltip, isUpgradeFormVisible } = this.state;
    const results = get(viewer, queryName);
    if (!get(results, 'edges.length')) {
      return (
        <Item.Group>
          {showPageTitle && (<PageTitle subTitle="Search Results" title="RepVisits" category="Intersect" />)}
          <Item styleName="noResultsContainer">
            <Item.Content>
              <Item.Header styleName="noResultsHeader"><FormattedMessage {...messages.noResults} /></Item.Header>
              <Item.Description styleName="noResultsDescription">
                <FormattedMessage {...messages.tryAgain} />
              </Item.Description>
            </Item.Content>
          </Item>
        </Item.Group>
      );
    } else {
      const rows = results.edges.map((highSchool) => highSchool.node);
      return (
        <div data-cy="intermediate-highschool-results">
          {showPageTitle && (<PageTitle subTitle="Search Results" title="RepVisits" category="Intersect" />)}
          <h2 styleName="resultText">{resultsTitle}</h2>
          <div styleName="chooseSchool">
            <FormattedMessage {...messages.chooseSchoolName} />
          </div>
          <p styleName="description">
            <span styleName="showing-count">
              <FormattedMessage
                {...messages.showingCount}
                values={{
                  count: results.edges.length,
                  total: results.totalCount,
                }}
              />
            </span>
          </p>
          <div styleName="resultDescription">
            <FormattedMessage {...messages.resultDescription} />
          </div>
          <Table basic="very">
            <caption styleName="hidden"><FormattedMessage {...messages.chooseSchool} /></caption>
            <Table.Header>
              <Table.Row className="tableRow">
                <Table.HeaderCell className="tableSchoolHeader">
                  <FormattedMessage {...messages.schoolName} />
                </Table.HeaderCell>
                {
                  enabledFeatures.includes(REPVISITS_LOOKING_FOR_YOU_COUNT) && (
                  <Table.HeaderCell className="tableInterestedHeader" data-cy="repvisit-search-results-interested-students-heading">
                    <FormattedMessage {...messages.interested} />{' '}
                    <Icon
                      name="info circle"
                      tabindex=""
                      data-cy="repvisit-search-results-interested-students-tooltip-toggle"
                      onMouseOver={this.toggleTooltip}
                      onMouseOut={this.toggleTooltip}
                      onFocus={this.toggleTooltip}
                      onBlur={this.toggleTooltip}
                    />
                    {showTooltip && (
                      <Label role="tooltip" basic pointing="left" styleName="interested-tooltip" data-cy="repvisit-search-results-interested-students-tooltip">
                        <FormattedMessage {...messages.interestedtooltip} />
                      </Label>
                    )}
                  </Table.HeaderCell>
                  )
                }
                <Table.HeaderCell className="tableLocationHeader">
                  <FormattedMessage {...messages.schoolLocation} />
                </Table.HeaderCell>
                <Table.HeaderCell className="tableCountryHeader">
                  <FormattedMessage {...messages.schoolCountry} />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body data-cy="repvisit-search-results-table">
              {rows.map((row, index) => (
                <Table.Row key={row.id}>
                  <Table.Cell styleName="rowName">
                    <h3>
                      <Link onClick={this.handleScrollPosition} data-cy={row.name} to={getLocation(row)}>{row.name}</Link>
                    </h3>
                  </Table.Cell>
                  {
                    enabledFeatures.includes(REPVISITS_LOOKING_FOR_YOU_COUNT) && (
                    <React.Fragment>
                      <SubscriptionActive anyOf={[REP_VISITS_SUBSCRIPTION]}>
                        <Table.Cell styleName="center" data-cy="repvisit-search-results-interested-students">
                          {row.interestedStudents < 10 ? (
                            'N/A'
                          ) : (
                            <div styleName="interested-students">
                              <span>{row.interestedStudents}</span>
                            </div>
                          )}
                        </Table.Cell>
                      </SubscriptionActive>
                      <SubscriptionInactive allOf={[REP_VISITS_SUBSCRIPTION]}>
                        {index === 0 && (
                          <td rowSpan={rows.length+1} styleName="locked" data-cy="repvisit-search-results-premium-lock">
                            <LockedContentComponent
                              body={intl.formatMessage(messages.interestedunlock)}
                              toggleUpgradeFormVisibility={this.toggleUpgradeFormVisibility}
                            />
                          </td>
                        )}
                      </SubscriptionInactive>
                    </React.Fragment>
                    )
                  }
                  <Table.Cell styleName="fieldCountryName">
                    {formatLocationByPath(row, 'address.field_hs_elna_city', 'address.field_hs_elna_state_latest')}<br />
                    <p styleName="countyItalic">
                      {row.address.field_hs_el_county_name ?
                        (this.affixCounty(row.address.field_hs_el_county_name)).toLowerCase() : row.address.field_hs_el_county_name}
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    {row.address.field_hs_na_country_name}
                  </Table.Cell>
                </Table.Row>
              ))}
              {/* In case of only one result, show an additional row to improve lock column design */}
              {
                enabledFeatures.includes(REPVISITS_LOOKING_FOR_YOU_COUNT) && (
                <SubscriptionInactive allOf={[REP_VISITS_SUBSCRIPTION]}>
                  {rows.length === 1 && (<Table.Row><Table.Cell style={{ height: '67px' }}>{' '}</Table.Cell></Table.Row>)}
                </SubscriptionInactive>
                )
              }
            </Table.Body>
          </Table>
          {get(results, 'pageInfo.hasNextPage') ? (
            <Container textAlign="center" styleName="viewButton">
              <Loader inline active={this.state.loading} />
              <Button size="small" primary onClick={this.loadMore}>
                <FormattedMessage {...messages.moreResults} />
              </Button>
            </Container>
          ): null}
          {isUpgradeFormVisible && (
            <SubscriptionUpgradeForm
              open
              subscription="interestedstudents"
              onClose={this.toggleUpgradeFormVisibility}
            />
          )}
        </div>
      );
    }
  }
}

export default injectIntl(SimplifiedIntermediateHighSchoolResults);
